import React, { createContext, useState, useEffect, useContext } from "react";
import useMarkerVisibility from "~/js/utilities/customHooks/useMarkerVisibility";
import { calculateNearestPoint } from "~/js/utilities/calculations/calculateNearestPoint";
import { calculateDistance } from "~/js/utilities/calculations/calculateDistance";

export const MapGuiContext = createContext();
export const MapInteractionContext = createContext();

const initialVisibility = ["startMarker", "endMarker"];

const MapInteractionProvider = ({ children }) => {
  const { trackData, setFitTrackBounds, setFitTracksBounds, splitsAuto } =
    useContext(MapGuiContext);

  const [zoom, setZoom] = useState(-1);
  const zoomRef = React.useRef(-1);
  const [trackSegment, setTrackSegment] = useState(null);
  const [clickedMarker, setClickedMarker] = useState(null);
  const [clickedTrackPoint, setClickedTrackPoint] = useState(null);
  const [split, setSplit] = useState({ km: 1, index: 0 });
  const [mapCenter, setMapCenter] = useState(null);
  const [mapBounds, setMapBounds] = useState({});
  const [hoveredTrackPoint, setHoveredTrackPoint] = useState(null);
  const [visibleBounds, setVisibleBounds] = useState(null);

  const handleSplitClick = React.useCallback(
    (points, splitItem, latlng, bounds = {}) => {
      if (!points) return;

      const startIndex = calculateNearestPoint(points, {
        lat: latlng.startLat,
        lng: latlng.startLng,
      }).index;
      const endIndex = calculateNearestPoint(points, {
        lat: latlng.endLat,
        lng: latlng.endLng,
      }).index;

      setTrackSegment({
        stats: splitItem,
        points: points.slice(startIndex, endIndex),
      });
      setMapBounds(bounds);
    },
    [],
  );

  const handleMarkerClick = React.useCallback((value) => {
    setClickedMarker(value);
  }, []);

  const handleMapClick = React.useCallback((value) => {
    setClickedMarker(null);
    setClickedTrackPoint(null);
    setMapCenter(null);
  }, []);

  const handleTrackClick = React.useCallback(
    (clickedLatLng) => {
      if (
        !trackData ||
        !trackData.points.track ||
        trackData.points.track.length === 0
      ) {
        return;
      }

      const nearestPoint = calculateNearestPoint(
        trackData.points.track,
        clickedLatLng,
      );
      if (nearestPoint !== null) {
        let totalDistanceRaw = 0;
        for (let i = 0; i < nearestPoint.index; i++) {
          const dist = calculateDistance(
            trackData.points.track[i].lat,
            trackData.points.track[i].lng,
            trackData.points.track[i + 1].lat,
            trackData.points.track[i + 1].lng,
          );
          if (!isNaN(dist)) {
            totalDistanceRaw += dist;
          }
        }
        setClickedTrackPoint({
          ...nearestPoint,
          distance: parseInt(totalDistanceRaw),
          distanceKm: parseInt(totalDistanceRaw / 1000),
        });
      }
    },
    [trackData],
  );

  const handleZoomed = React.useCallback(
    (value, isProgrammatic = false) => {
      if (
        value !== zoomRef.current &&
        zoomRef.current > -1 &&
        !isProgrammatic
      ) {
        setFitTrackBounds(false);
        setFitTracksBounds(false);
      }
      zoomRef.current = value;
      setZoom(value);
    },
    [setFitTrackBounds, setFitTracksBounds],
  );

  const handleMapDragged = React.useCallback(() => {
    setFitTrackBounds(false);
    setFitTracksBounds(false);
  }, [setFitTrackBounds, setFitTracksBounds]);

  useEffect(() => {
    if (splitsAuto && zoom > -1) {
      handleZoomed(zoom);
    }
  }, [splitsAuto, zoom, handleZoomed]);

  useEffect(() => {
    if (splitsAuto && zoom > -1 && trackData?.splits) {
      const newSplitData = trackData.splits.findLast(
        (splitZoom) => splitZoom.zoom >= zoom,
      );
      setSplit((prevSplit) => {
        const newSplit = newSplitData
          ? {
              km: newSplitData.split,
              index: trackData.splits.indexOf(newSplitData),
            }
          : null;

        if (prevSplit === null && newSplit === null) return null;
        if (
          prevSplit &&
          newSplit &&
          prevSplit.km === newSplit.km &&
          prevSplit.index === newSplit.index
        ) {
          return prevSplit;
        }
        return newSplit;
      });
    }
  }, [zoom, splitsAuto, trackData]);

  useEffect(() => {
    if (trackData !== null) {
      setTrackSegment(null);
      setClickedTrackPoint(null);
    }
  }, [trackData]);

  return (
    <MapInteractionContext.Provider
      value={{
        zoom,
        setZoom,
        trackSegment,
        setTrackSegment,
        clickedMarker,
        setClickedMarker,
        clickedTrackPoint,
        setClickedTrackPoint,
        split,
        setSplit,
        mapCenter,
        setMapCenter,
        mapBounds,
        setMapBounds,
        handleSplitClick,
        handleMarkerClick,
        handleMapClick,
        handleTrackClick,
        handleZoomed,
        handleMapDragged,
        hoveredTrackPoint,
        setHoveredTrackPoint,
        visibleBounds,
        setVisibleBounds,
      }}
    >
      {children}
    </MapInteractionContext.Provider>
  );
};

export const MapGuiProvider = ({
  children,
  track,
  trackData,
  isReversed,
  toggleReverse,
}) => {
  const [mapType, setMapType] = useState("roadmap");
  const [stopsThreshold, setStopsThreshold] = useState(60);
  const [fitTrackBounds, setFitTrackBounds] = useState(true);
  const [fitTracksBounds, setFitTracksBounds] = useState(false);
  const [splitsAuto, setSplitsAuto] = useState(true);
  const [trackOverlaysVisibility, setTrackOverlaysVisibility] = useState(true);
  const [tracksVisibility, setTracksVisibility] = useState(true);
  const [trackVisibility, setTrackVisibility] = useState(true);
  const [syncMapProfile, setSyncMapProfile] = useState(false);
  const [markerVisibility, toggleMarkerVisibility] =
    useMarkerVisibility(initialVisibility);

  const handleFitBoundsClick = () => {
    setFitTrackBounds(true);
    setFitTracksBounds(false);
  };

  const handleFitTracksBoundsClick = () => {
    setFitTracksBounds(true);
    setFitTrackBounds(false);
  };

  useEffect(() => {
    if (trackData !== null) {
      setFitTrackBounds(true);
      setFitTracksBounds(false);
    }
  }, [trackData]);

  return (
    <MapGuiContext.Provider
      value={{
        track,
        trackData,
        mapType,
        setMapType,
        stopsThreshold,
        setStopsThreshold,
        fitTrackBounds,
        fitTracksBounds,
        setFitTrackBounds,
        setFitTracksBounds,
        splitsAuto,
        setSplitsAuto,
        markerVisibility,
        toggleMarkerVisibility,
        trackOverlaysVisibility,
        setTrackOverlaysVisibility,
        handleFitBoundsClick,
        handleFitTracksBoundsClick,
        isReversed,
        toggleReverse,
        tracksVisibility,
        setTracksVisibility,
        trackVisibility,
        setTrackVisibility,
        syncMapProfile,
        setSyncMapProfile,
        fitTrackBounds,
      }}
    >
      <MapInteractionProvider>{children}</MapInteractionProvider>
    </MapGuiContext.Provider>
  );
};
